import { useParams, Link } from 'react-router-dom';
import WebsiteLayout from '@/components/layout/WebsiteLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ArrowLeft, 
  ArrowRight, 
  Code, 
  Database, 
  Cloud, 
  Shield, 
  Smartphone, 
  Brain,
  CheckCircle2,
  Zap,
  Users,
  TrendingUp,
  UserCheck,
  Cog,
  Bot,
  Target,
  Settings,
  ClipboardCheck,
  FileCheck,
  Building2,
  TestTube,
  Bug,
  Lock
} from 'lucide-react';

const services = {
  'ai-staffing': {
    title: 'AI-Powered IT Staffing',
    subtitle: 'Contract and Long-Term IT Professionals for AI-Assisted Delivery',
    icon: UserCheck,
    heroImage: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200',
    description: 'Contract and long-term IT professionals trained to work with AI-assisted delivery environments. Faster output, lower manual overhead, better consistency. Our engineers are equipped to leverage AI tools for enhanced productivity.',
    features: [
      {
        title: 'AI Integration Engineers',
        description: 'Specialists who integrate AI tools and models into existing systems and workflows for enhanced automation.',
        icon: Bot
      },
      {
        title: 'Cloud & Platform Specialists',
        description: 'Experts in cloud infrastructure, DevOps, and platform engineering with AI-first approaches.',
        icon: Cloud
      },
      {
        title: 'QA Automation Engineers',
        description: 'Quality assurance professionals who leverage AI for test automation and validation.',
        icon: CheckCircle2
      },
      {
        title: 'Data & Analytics Engineers',
        description: 'Data professionals skilled in building AI-ready data pipelines and analytics solutions.',
        icon: Database
      }
    ],
    benefits: ['Faster output with AI tools', 'Lower manual overhead', 'Better consistency', 'Modern delivery pipelines', 'Scalable teams'],
    technologies: ['Python', 'TensorFlow', 'AWS', 'Azure', 'Kubernetes', 'Docker', 'GitHub Copilot', 'LangChain']
  },
  'managed-teams': {
    title: 'Managed Delivery Pods',
    subtitle: 'Pre-structured Teams Delivering Defined Outcomes',
    icon: Users,
    heroImage: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200',
    description: 'Outcome-based delivery pods instead of hourly effort. Get complete teams structured for specific delivery outcomes - from AI rollout support to platform modernization.',
    features: [
      {
        title: 'Automation Pods',
        description: 'Dedicated teams for workflow automation, RPA implementation, and process optimization.',
        icon: Cog
      },
      {
        title: 'AI Testing Teams',
        description: 'Specialized squads for AI model validation, testing, and quality assurance.',
        icon: TestTube
      },
      {
        title: 'Cloud Migration Squads',
        description: 'Teams focused on seamless cloud migration and infrastructure modernization.',
        icon: Cloud
      },
      {
        title: 'Data Engineering Pods',
        description: 'End-to-end data pipeline teams for building AI-ready data infrastructure.',
        icon: Database
      }
    ],
    benefits: ['Defined outcomes', 'Reduced management overhead', 'Faster ramp-up', 'Scalable capacity', 'Risk mitigation'],
    technologies: ['Agile', 'DevOps', 'CI/CD', 'Terraform', 'Ansible', 'Jenkins', 'GitLab']
  },
  'ai-integration': {
    title: 'AI & Data Engineering Support',
    subtitle: 'Technical Specialists Supporting AI Systems and Data Platforms',
    icon: Cog,
    heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200',
    description: 'We help companies implement AI tools into real workflows — not just pilots. Technical specialists supporting AI systems and data platforms with production-ready implementations.',
    features: [
      {
        title: 'AI Tool Onboarding',
        description: 'Implementation and integration of AI tools like Copilot, ChatGPT, and custom models into your workflows.',
        icon: Bot
      },
      {
        title: 'Workflow Automation',
        description: 'End-to-end automation of business processes using AI and intelligent automation.',
        icon: Zap
      },
      {
        title: 'AI Testing & Validation',
        description: 'Comprehensive testing frameworks for AI models including bias detection and accuracy validation.',
        icon: CheckCircle2
      },
      {
        title: 'Human-in-the-Loop Controls',
        description: 'Design and implementation of oversight mechanisms for AI-driven decisions.',
        icon: Users
      }
    ],
    benefits: ['Real workflow integration', 'Production-ready AI', 'Measurable ROI', 'Risk management', 'Continuous optimization'],
    technologies: ['OpenAI', 'LangChain', 'Hugging Face', 'MLflow', 'Airflow', 'Spark', 'Databricks']
  },
  'qa-testing': {
    title: 'QA & AI Testing Services',
    subtitle: 'Modern Testing Including Automation and AI Validation',
    icon: TestTube,
    heroImage: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200',
    description: 'Modern testing that includes automation and AI validation scenarios. Our QA teams combine traditional testing expertise with AI-specific validation capabilities.',
    features: [
      {
        title: 'Test Automation',
        description: 'Comprehensive test automation frameworks using modern tools and AI-assisted test generation.',
        icon: Cog
      },
      {
        title: 'AI Output Validation',
        description: 'Specialized testing for AI model outputs, including accuracy, bias, and consistency checks.',
        icon: Brain
      },
      {
        title: 'Regression Frameworks',
        description: 'Intelligent regression testing that adapts to code changes and prioritizes critical paths.',
        icon: Target
      },
      {
        title: 'Performance Testing',
        description: 'Load testing and performance optimization for AI-powered applications.',
        icon: TrendingUp
      }
    ],
    benefits: ['Faster test cycles', 'Higher coverage', 'AI-specific validation', 'Reduced manual effort', 'Continuous quality'],
    technologies: ['Selenium', 'Cypress', 'Playwright', 'JMeter', 'Pytest', 'Jest', 'AI Testing Tools']
  },
  'ai-governance': {
    title: 'Compliance & Regulated Delivery Support',
    subtitle: 'Delivery Teams Experienced in Control-Driven Environments',
    icon: Shield,
    heroImage: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1200',
    description: 'AI adoption requires control and auditability. We provide delivery support aligned with compliance needs for regulated industries including banking, finance, and healthcare.',
    features: [
      {
        title: 'AI Documentation Support',
        description: 'Comprehensive documentation for AI systems including model cards, data sheets, and audit trails.',
        icon: FileCheck
      },
      {
        title: 'Model Validation Processes',
        description: 'Structured validation frameworks for AI models meeting regulatory requirements.',
        icon: CheckCircle2
      },
      {
        title: 'Audit Evidence Preparation',
        description: 'Preparation of audit-ready documentation and evidence for regulatory examinations.',
        icon: ClipboardCheck
      },
      {
        title: 'Risk & Control Mapping',
        description: 'Identification and documentation of AI-related risks with appropriate controls.',
        icon: Shield
      }
    ],
    benefits: ['Regulatory compliance', 'Audit readiness', 'Risk mitigation', 'Control frameworks', 'Documentation standards'],
    technologies: ['GRC Tools', 'NIST AI RMF', 'ISO 27001', 'SOC 2', 'GDPR', 'PDPA', 'Industry Standards']
  },
  // Keep legacy routes working
  'application-development': {
    title: 'AI-Powered IT Staffing',
    subtitle: 'Contract and Long-Term IT Professionals for AI-Assisted Delivery',
    icon: UserCheck,
    heroImage: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200',
    description: 'Contract and long-term IT professionals trained to work with AI-assisted delivery environments. Faster output, lower manual overhead, better consistency.',
    features: [
      {
        title: 'AI Integration Engineers',
        description: 'Specialists who integrate AI tools and models into existing systems and workflows.',
        icon: Bot
      },
      {
        title: 'Cloud & Platform Specialists',
        description: 'Experts in cloud infrastructure, DevOps, and platform engineering.',
        icon: Cloud
      },
      {
        title: 'QA Automation Engineers',
        description: 'Quality assurance professionals who leverage AI for test automation.',
        icon: CheckCircle2
      },
      {
        title: 'Data & Analytics Engineers',
        description: 'Data professionals skilled in building AI-ready data pipelines.',
        icon: Database
      }
    ],
    benefits: ['Faster output with AI tools', 'Lower manual overhead', 'Better consistency', 'Modern delivery pipelines', 'Scalable teams'],
    technologies: ['Python', 'TensorFlow', 'AWS', 'Azure', 'Kubernetes', 'Docker', 'GitHub Copilot', 'LangChain']
  },
  'data-analytics': {
    title: 'AI & Data Engineering Support',
    subtitle: 'Technical Specialists Supporting AI Systems and Data Platforms',
    icon: Brain,
    heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200',
    description: 'We help companies implement AI tools into real workflows — not just pilots. Technical specialists supporting AI systems and data platforms.',
    features: [
      {
        title: 'AI Tool Onboarding',
        description: 'Implementation of AI tools like Copilot, ChatGPT, and custom models.',
        icon: Bot
      },
      {
        title: 'Workflow Automation',
        description: 'End-to-end automation using AI and intelligent automation.',
        icon: Zap
      },
      {
        title: 'AI Testing & Validation',
        description: 'Testing frameworks for AI models including bias detection.',
        icon: CheckCircle2
      },
      {
        title: 'Data Pipelines',
        description: 'Building AI-ready data infrastructure and pipelines.',
        icon: Database
      }
    ],
    benefits: ['Real workflow integration', 'Production-ready AI', 'Measurable ROI', 'Risk management', 'Continuous optimization'],
    technologies: ['OpenAI', 'LangChain', 'Hugging Face', 'MLflow', 'Airflow', 'Spark', 'Databricks']
  },
  'cloud-solutions': {
    title: 'Managed Delivery Pods',
    subtitle: 'Pre-structured Teams Delivering Defined Outcomes',
    icon: Cloud,
    heroImage: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200',
    description: 'Outcome-based delivery pods instead of hourly effort. Complete teams structured for specific delivery outcomes.',
    features: [
      {
        title: 'Cloud Migration Squads',
        description: 'Teams focused on cloud migration and infrastructure modernization.',
        icon: Cloud
      },
      {
        title: 'Automation Pods',
        description: 'Dedicated teams for workflow automation and RPA.',
        icon: Cog
      },
      {
        title: 'AI Testing Teams',
        description: 'Squads for AI model validation and testing.',
        icon: TestTube
      },
      {
        title: 'Data Engineering Pods',
        description: 'Teams for building AI-ready data infrastructure.',
        icon: Database
      }
    ],
    benefits: ['Defined outcomes', 'Reduced overhead', 'Faster ramp-up', 'Scalable capacity', 'Risk mitigation'],
    technologies: ['AWS', 'Azure', 'GCP', 'Kubernetes', 'Terraform', 'Jenkins', 'GitLab CI']
  },
  'cybersecurity': {
    title: 'Compliance & Regulated Delivery Support',
    subtitle: 'Delivery Teams for Control-Driven Environments',
    icon: Shield,
    heroImage: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1200',
    description: 'AI adoption requires control and auditability. We provide delivery support aligned with compliance needs.',
    features: [
      {
        title: 'AI Documentation Support',
        description: 'Documentation for AI systems including model cards and audit trails.',
        icon: FileCheck
      },
      {
        title: 'Model Validation',
        description: 'Validation frameworks meeting regulatory requirements.',
        icon: CheckCircle2
      },
      {
        title: 'Audit Preparation',
        description: 'Audit-ready documentation for regulatory examinations.',
        icon: ClipboardCheck
      },
      {
        title: 'Risk & Control Mapping',
        description: 'AI-related risks with appropriate controls.',
        icon: Shield
      }
    ],
    benefits: ['Regulatory compliance', 'Audit readiness', 'Risk mitigation', 'Control frameworks', 'Documentation standards'],
    technologies: ['NIST AI RMF', 'ISO 27001', 'SOC 2', 'GDPR', 'PDPA', 'Industry Standards']
  },
  'mobile-development': {
    title: 'QA & AI Testing Services',
    subtitle: 'Modern Testing with Automation and AI Validation',
    icon: TestTube,
    heroImage: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200',
    description: 'Modern testing that includes automation and AI validation scenarios.',
    features: [
      {
        title: 'Test Automation',
        description: 'Comprehensive test automation frameworks.',
        icon: Cog
      },
      {
        title: 'AI Output Validation',
        description: 'Testing for AI model outputs and accuracy.',
        icon: Brain
      },
      {
        title: 'Regression Frameworks',
        description: 'Intelligent regression testing.',
        icon: Target
      },
      {
        title: 'Performance Testing',
        description: 'Load testing for AI-powered applications.',
        icon: TrendingUp
      }
    ],
    benefits: ['Faster test cycles', 'Higher coverage', 'AI-specific validation', 'Reduced manual effort', 'Continuous quality'],
    technologies: ['Selenium', 'Cypress', 'Playwright', 'JMeter', 'Pytest', 'Jest']
  },
  'digital-transformation': {
    title: 'AI Workforce Transformation',
    subtitle: 'Redesign Your Teams for the AI Era',
    icon: Zap,
    heroImage: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=1200',
    description: 'We help organizations redesign their technology teams for the AI era with comprehensive transformation services.',
    features: [
      {
        title: 'AI Impact Assessment',
        description: 'Analysis of AI impact on roles and responsibilities.',
        icon: TrendingUp
      },
      {
        title: 'Workforce Redesign',
        description: 'Planning for AI-powered team structures.',
        icon: Users
      },
      {
        title: 'Hybrid Team Models',
        description: 'AI + human team design and implementation.',
        icon: Bot
      },
      {
        title: 'Skill Gap Mapping',
        description: 'Identification of skills needed for AI adoption.',
        icon: Target
      }
    ],
    benefits: ['Future-ready teams', 'Increased productivity', 'Competitive advantage', 'Smooth transition', 'Measurable outcomes'],
    technologies: ['AI Tools', 'Change Management', 'Training Programs', 'Skill Assessment']
  }
};

export default function ServicePage() {
  const { serviceId } = useParams();
  const service = services[serviceId];
  
  if (!service) {
    return (
      <WebsiteLayout>
        <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold text-white">Service Not Found</h1>
            <Link to="/">
              <Button className="mt-8">Go Home</Button>
            </Link>
          </div>
        </section>
      </WebsiteLayout>
    );
  }
  
  const Icon = service.icon;
  
  return (
    <WebsiteLayout>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={service.heroImage} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900/80" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link to="/#services" className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Services
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-blue-600/20 flex items-center justify-center">
              <Icon className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <p className="text-blue-400 font-semibold tracking-wide uppercase text-sm">Our Services</p>
              <h1 className="text-4xl md:text-5xl font-bold text-white">{service.title}</h1>
            </div>
          </div>
          
          <p className="text-xl text-blue-200 max-w-2xl mb-8">{service.subtitle}</p>
          <p className="text-lg text-slate-300 max-w-3xl">{service.description}</p>
          
          <div className="flex flex-wrap gap-4 mt-8">
            <a href="/#contact">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                Get Started <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <Link to="/our-work">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                View Our Work
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Features */}
      <section className="py-20 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">What We Offer</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-12 max-w-2xl">
            Our comprehensive {service.title.toLowerCase()} services are designed to help your business thrive in the AI era.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {service.features.map((feature, index) => {
              const FeatureIcon = feature.icon;
              return (
                <Card key={index} className="border dark:border-slate-700 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
                      <FeatureIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{feature.title}</h3>
                    <p className="text-slate-600 dark:text-slate-400">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Benefits & Technologies */}
      <section className="py-20 bg-slate-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Benefits */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8">Benefits</h2>
              <ul className="space-y-4">
                {service.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-lg text-slate-700 dark:text-slate-300">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Technologies */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8">Technologies & Standards</h2>
              <div className="flex flex-wrap gap-3">
                {service.technologies.map((tech, index) => (
                  <span 
                    key={index}
                    className="px-4 py-2 bg-white dark:bg-slate-700 rounded-full text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-600 font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8">Let&apos;s discuss how our {service.title.toLowerCase()} solutions can transform your delivery.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/#contact">
              <Button size="lg" variant="secondary" className="text-blue-600">
                Contact Us <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <Link to="/our-work">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                View Our Work
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </WebsiteLayout>
  );
}
